// DeepSeek text/story adapter — mocked transport, timeout, retry, validation.
import { chatComplete, DEEPSEEK_BASE_URL } from '../llm/client.js';
import { isHostedSite } from '../llm/hosted.js';
import { resolveValidatedOutput } from '../prompts/compile.js';
import { directorError, type RecoverableAction, type StructuredDirectorError } from '../prompts/errors.js';
import { mergeManifests } from '../prompts/manifest.js';
import { parseProviderJson } from '../prompts/validate.js';
import type { PromptRequest } from '../prompts/types.js';
import { loadPersistedKey } from '../ui/secureStore.js';
import { fallbackNoticeForProvider } from './notices.js';
import { purposeQueue } from './queue.js';
import {
  assertNoSecretLeaks,
  mapTransportToDirectorError,
  type TransportOptions,
  TransportFailure,
  withRetry,
  withTimeout,
} from './transport.js';
import { buildTextGenerationRequest, type TextGenerationRequest } from './text.js';

export interface TextAdapterDeps {
  loadKey?: () => Promise<string>;
  transport?: TextTransport;
  transportOptions?: TransportOptions;
}

export interface TextTransport {
  complete(wire: TextGenerationRequest, apiKey: string, opts?: TransportOptions): Promise<string>;
}

export interface TextAdapterSuccess<T> {
  ok: true;
  data: T;
  manifest: PromptRequest['provenance'];
  fallbackUsed: boolean;
  fallbackNotice: string | null;
  providerAttempted: boolean;
}

export interface TextAdapterFailure<T> {
  ok: false;
  error: StructuredDirectorError;
  uiMessage: string;
  actions: RecoverableAction[];
  manifest: PromptRequest['provenance'];
  fallbackData?: T;
  saveDraftEligible: boolean;
}

export type TextAdapterResult<T> = TextAdapterSuccess<T> | TextAdapterFailure<T>;

export const defaultDeepSeekTransport: TextTransport = {
  async complete(wire, apiKey, opts = {}) {
    if (isHostedSite()) {
      return chatComplete(wire.messages, {
        model: wire.model,
        json: wire.json,
        maxTokens: wire.maxTokens,
        temperature: wire.temperature,
      });
    }
    const fetchImpl = opts.fetchImpl ?? fetch;
    const body = {
      model: wire.model,
      messages: wire.messages,
      stream: false,
      temperature: wire.temperature,
      max_tokens: wire.maxTokens,
      thinking: { type: 'disabled' },
      ...(wire.json ? { response_format: { type: 'json_object' as const } } : {}),
    };

    const response = await fetchImpl(`${DEEPSEEK_BASE_URL}/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new TransportFailure(`DeepSeek HTTP ${response.status}`);
    const data = (await response.json()) as {
      choices?: Array<{ message?: { content?: string } }>;
    };
    const text = data.choices?.[0]?.message?.content ?? '';
    if (!text.trim()) throw new TransportFailure('Empty DeepSeek response');
    return text;
  },
};

async function callTextTransport(
  transport: TextTransport,
  wire: TextGenerationRequest,
  apiKey: string,
  opts: TransportOptions = {},
): Promise<string> {
  const timeoutMs = opts.timeoutMs ?? 30_000;
  return withRetry(
    () =>
      withTimeout(transport.complete(wire, apiKey, opts), timeoutMs, 'deepseek'),
    opts,
  );
}

function applyFallback<T>(request: PromptRequest, notice: string): TextAdapterSuccess<T> {
  const data = request.fallback.local() as T;
  const manifest = {
    ...request.provenance,
    provider: 'local' as const,
    fallbackUsed: true,
    validationStatus: 'fallback' as const,
  };
  assertNoSecretLeaks(manifest, []);
  return {
    ok: true,
    data,
    manifest,
    fallbackUsed: true,
    fallbackNotice: notice,
    providerAttempted: false,
  };
}

function purposeHeaderOnly(instructions: string): string {
  const split = instructions.indexOf('\n\n');
  return split >= 0 ? instructions.slice(0, split) : instructions;
}

function buildValidationRetryWire(
  wire: TextGenerationRequest,
  request: PromptRequest,
  validationError: StructuredDirectorError,
): TextGenerationRequest {
  const reducedInstructions = [
    purposeHeaderOnly(request.instructions),
    'VALIDATION_ERROR_FROM_PRIOR_ATTEMPT:',
    validationError.message,
    ...(validationError.details.length
      ? validationError.details.map((d) => `- ${d}`)
      : []),
    `Required schema: ${request.outputSchemaId}.`,
    'Correct the prior response and return ONLY valid JSON.',
  ].join('\n');

  return {
    ...wire,
    messages: wire.messages.map((m) =>
      m.role === 'user' ? { ...m, content: reducedInstructions } : m,
    ),
  };
}

function allowsNoKeyLocalFallback(request: PromptRequest): boolean {
  if (!request.fallback.noKeyUsesLocal) return false;
  if (request.meta.purpose === 'world_devices') return false;
  if (request.persistencePolicy === 'world') return false;
  return true;
}

function assertWorldDevicesGate(request: PromptRequest): StructuredDirectorError | null {
  if (request.meta.purpose !== 'world_devices') return null;
  if (request.simulation.mode === 'creating') {
    return directorError(
      'missing_required',
      'Twenty world devices require a confirmed review snapshot before generation.',
      { schemaId: request.outputSchemaId, actions: ['user_choice'] },
    );
  }
  if (!request.instructions.includes('CONFIRMED_SNAPSHOT')) {
    return directorError(
      'missing_required',
      'Twenty world devices require a confirmed review snapshot before generation.',
      { schemaId: request.outputSchemaId, actions: ['user_choice'] },
    );
  }
  return null;
}

export async function executeTextProvider<T = unknown>(
  request: PromptRequest,
  deps: TextAdapterDeps = {},
): Promise<TextAdapterResult<T>> {
  const gate = assertWorldDevicesGate(request);
  if (gate) {
    return {
      ok: false,
      error: gate,
      uiMessage: gate.message,
      actions: gate.actions,
      manifest: { ...request.provenance, validationStatus: 'invalid' },
      saveDraftEligible: request.persistencePolicy === 'draft',
    };
  }

  const loadKey = deps.loadKey ?? (() => loadPersistedKey('deepseek'));
  const apiKey = (await loadKey()).trim();
  const transport = deps.transport ?? defaultDeepSeekTransport;

  if (!apiKey && !isHostedSite()) {
    if (allowsNoKeyLocalFallback(request)) {
      return applyFallback<T>(request, fallbackNoticeForProvider('deepseek'));
    }
    const err = directorError('missing_required', 'DeepSeek API key required for AI completion.', {
      actions: ['user_choice'],
    });
    return {
      ok: false,
      error: err,
      uiMessage: err.message,
      actions: err.actions,
      manifest: { ...request.provenance, validationStatus: 'invalid' },
      saveDraftEligible: request.persistencePolicy === 'draft',
    };
  }

  const wire = buildTextGenerationRequest(request);
  assertNoSecretLeaks(wire, [apiKey]);

  return purposeQueue(request.meta.purpose).run(async () => {
    let manifest = { ...request.provenance };
    let rawText: string;
    try {
      rawText = await callTextTransport(transport, wire, apiKey, deps.transportOptions);
    } catch (e) {
      const error = mapTransportToDirectorError(e);
      const fallbackData = request.fallback.local() as T;
      return {
        ok: false,
        error,
        uiMessage: error.message,
        actions: error.actions,
        manifest: { ...manifest, validationStatus: 'invalid' },
        fallbackData,
        saveDraftEligible: request.persistencePolicy === 'draft',
      };
    }

    assertNoSecretLeaks(rawText, [apiKey]);

    const parseOnce = (text: string) => {
      const parsed = parseProviderJson(text, request.outputSchemaId);
      if (!parsed.ok) {
        return {
          ok: false as const,
          error: parsed.error,
          uiMessage: parsed.error.message,
          manifest: { ...request.provenance, validationStatus: 'invalid' as const },
        };
      }
      return resolveValidatedOutput<T>(request, parsed.value);
    };

    let validated = parseOnce(rawText);
    if (!validated.ok && request.fallback.retryOnValidationError) {
      const retryWire = buildValidationRetryWire(wire, request, validated.error);
      try {
        rawText = await callTextTransport(transport, retryWire, apiKey, deps.transportOptions);
        assertNoSecretLeaks(rawText, [apiKey]);
        const retryValidated = parseOnce(rawText);
        manifest = mergeManifests(manifest, {
          ...manifest,
          validationStatus: retryValidated.ok ? 'valid' : 'invalid',
        });
        validated = retryValidated;
      } catch {
        // Retry transport failure falls through to validation branch below.
      }
    }

    if (validated.ok) {
      assertNoSecretLeaks(validated.manifest, [apiKey]);
      return {
        ok: true,
        data: validated.data,
        manifest: validated.manifest,
        fallbackUsed: false,
        fallbackNotice: null,
        providerAttempted: true,
      };
    }

    const fallbackData = request.fallback.local() as T;
    return {
      ok: false,
      error: validated.error,
      uiMessage: validated.uiMessage,
      actions: validated.error.actions,
      manifest: validated.manifest,
      fallbackData,
      saveDraftEligible: request.persistencePolicy === 'draft',
    };
  });
}
